/**
 * Shared predicate editors (Release 1 A). The search facet panel (`../FacetPanel.tsx`) and the board filter bar
 * (`features/grid/FilterBar.tsx`) render the same controls; both hand a `SearchFilters`/`BoardFilters` object in
 * and get the next one back through `onChange`. Nothing here knows about URLs or queries.
 */
import { useState, type ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import type { CustomField, Facets, SearchFilters } from '@/lib/api/types';
import { usePeople } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { Input, Select } from '@/components/Input';
import { cx } from '@/lib/format';
import { DATE_PRESETS, datePresetOf, presetRange, type BoardFilters } from './serialize';

export type Bucket = { value: string; label?: string; count: number };
/** Both the search panel (`SearchFilters`) and the board bar (`BoardFilters`) satisfy this. */
export type AnyFilters = BoardFilters | SearchFilters;

/** Checkbox list with counts; selected values stay visible even when the facet no longer returns them. */
export function Buckets({ buckets, selected, onToggle, limit = 8, labelOf }: { buckets: Bucket[] | undefined; selected: string[]; onToggle: (v: string) => void; limit?: number; labelOf?: (v: string) => string }) {
  const [more, setMore] = useState(false);
  const list = buckets ?? [];
  const shown = more ? list : list.slice(0, limit);
  const extra = selected.filter((s) => !list.some((b) => b.value === s)).map((value) => ({ value, count: 0 }));
  if (list.length === 0 && extra.length === 0) return <span className="text-xs text-fg-subtle">—</span>;
  return (
    <ul className="space-y-0.5 text-xs">
      {[...shown, ...extra].map((b) => {
        const on = selected.includes(b.value);
        return (
          <li key={b.value}>
            <label className={cx('flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-bg-sunken', on && 'text-accent')}>
              <input type="checkbox" checked={on} onChange={() => onToggle(b.value)} />
              <span className="min-w-0 flex-1 truncate">{(b as Bucket).label ?? labelOf?.(b.value) ?? b.value}</span>
              <span className="text-fg-subtle tabular-nums">{b.count}</span>
            </label>
          </li>
        );
      })}
      {list.length > limit && <li><button type="button" className="px-1 text-accent hover:underline" onClick={() => setMore((m) => !m)}>{more ? 'less' : `+${list.length - limit} more`}</button></li>}
    </ul>
  );
}

/** Facet swatches plus a custom hex picker. */
export function ColorSwatches<F extends AnyFilters>({ facets, filters, onChange }: { facets: Facets['colors'] | undefined; filters: F; onChange: (f: F) => void }) {
  const [custom, setCustom] = useState('#');
  const colorHexes = new Set((filters.colors ?? []).map((c) => c.hex.toLowerCase()));
  const toggleColor = (hex: string) => {
    const h = hex.toLowerCase();
    onChange({ ...filters, colors: colorHexes.has(h) ? (filters.colors ?? []).filter((c) => c.hex.toLowerCase() !== h) : [...(filters.colors ?? []), { hex: h, tolerance: 0.15 }] });
  };
  return (
    <div className="flex flex-wrap gap-1">
      {(facets ?? []).map((c) => (
        <button key={c.hex} type="button" aria-pressed={colorHexes.has(c.hex.toLowerCase())} aria-label={`Colour ${c.hex}, ${c.count} assets`} title={`${c.hex} · ${c.count}`} onClick={() => toggleColor(c.hex)} className={cx('h-6 w-6 rounded border', colorHexes.has(c.hex.toLowerCase()) ? 'border-accent ring-2 ring-accent/40' : 'border-line')} style={{ background: c.hex }} />
      ))}
      {(filters.colors ?? []).filter((c) => !(facets ?? []).some((f) => f.hex.toLowerCase() === c.hex.toLowerCase())).map((c) => (
        <button key={c.hex} type="button" aria-pressed aria-label={`Colour ${c.hex}, selected`} title={c.hex} onClick={() => toggleColor(c.hex)} className="h-6 w-6 rounded border border-accent ring-2 ring-accent/40" style={{ background: c.hex }} />
      ))}
      <form
        className="flex items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (/^#[0-9a-f]{6}$/i.test(custom)) {
            toggleColor(custom);
            setCustom('#');
          }
        }}
      >
        <input type="color" aria-label="Pick a colour" value={/^#[0-9a-f]{6}$/i.test(custom) ? custom : '#888888'} onChange={(e) => setCustom(e.target.value)} className="h-6 w-6 cursor-pointer rounded border border-line bg-transparent p-0" />
        <Input value={custom} onChange={(e) => setCustom(e.target.value)} className="h-6 w-20 px-1 text-xs" aria-label="Hex colour" />
        <button type="submit" aria-label="Add colour" className="text-fg-subtle hover:text-fg"><Plus size={14} /></button>
      </form>
    </div>
  );
}

export function PersonFace({ id }: { id: string }) {
  // Representative face crop: the facet bucket carries only the cluster id; the people query holds the thumbnail.
  const wsId = useSessionStore((s) => s.workspace?.id);
  const people = usePeople(wsId);
  const url = people.data?.find((p) => p.id === id)?.sample_thumbnail_url;
  return url ? <img src={url} alt="" className="h-full w-full object-cover" data-cluster={id} /> : <span className="block h-full w-full bg-gradient-to-br from-accent/40 to-accent/10" data-cluster={id} />;
}

/** People chips (face + name + count). Falls back to the people catalogue when the facet is empty. */
export function PeopleChips({ buckets, selected, onToggle, fallback }: { buckets: Bucket[] | undefined; selected: string[]; onToggle: (id: string) => void; fallback?: Bucket[] }) {
  const list = buckets?.length ? buckets : (fallback ?? []);
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((b) => {
        const on = selected.includes(b.value);
        return (
          <button key={b.value} type="button" aria-pressed={on} onClick={() => onToggle(b.value)} title={`${b.label ?? b.value} · ${b.count}`} className={cx('flex items-center gap-1 rounded-full border py-0.5 pr-2 pl-0.5 text-xs', on ? 'border-accent bg-accent-soft text-accent' : 'border-line hover:bg-bg-sunken')}>
            <span className="h-5 w-5 overflow-hidden rounded-full bg-bg-sunken"><PersonFace id={b.value} /></span>
            {b.label ?? 'Unnamed'}
            <span className="text-fg-subtle">{b.count}</span>
          </button>
        );
      })}
      {list.length === 0 && <span className="text-xs text-fg-subtle">—</span>}
    </div>
  );
}

/** `tag_match` any/all, shown once more than one tag is selected. */
export function TagMatchSelect<F extends AnyFilters>({ filters, onChange }: { filters: F; onChange: (f: F) => void }) {
  if ((filters.tag_ids?.length ?? 0) < 2) return null;
  return (
    <label className="mt-1 flex items-center gap-2 text-xs text-fg-muted">
      match
      <Select value={filters.tag_match ?? 'any'} onChange={(e) => onChange({ ...filters, tag_match: e.target.value as 'all' | 'any' })} className="h-6 w-auto py-0 text-xs">
        <option value="any">any</option>
        <option value="all">all</option>
      </Select>
    </label>
  );
}

/** Quick ranges (today / 7 days / 30 days / this year); the selected one is `aria-pressed`. */
export function DatePresetButtons<F extends AnyFilters>({ filters, onChange, now }: { filters: F; onChange: (f: F) => void; now?: Date }) {
  const current = datePresetOf(filters.date, now);
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label="Date presets">
      {DATE_PRESETS.map((p) => (
        <button key={p.id} type="button" aria-pressed={current === p.id} onClick={() => onChange(current === p.id ? { ...filters, date: undefined } : { ...filters, date: { field: filters.date?.field ?? 'created_at', ...presetRange(p.id, now) } })} className={cx('rounded-full border px-2 py-0.5 text-xs', current === p.id ? 'border-accent bg-accent-soft text-accent' : 'border-line hover:bg-bg-sunken')}>
          {p.label}
        </button>
      ))}
    </div>
  );
}

/** Field + from/to inputs (the "custom" range). */
export function DateRangeFields<F extends AnyFilters>({ filters, onChange, fields, children }: { filters: F; onChange: (f: F) => void; fields: readonly CustomField[]; children?: ReactNode }) {
  const field = filters.date?.field ?? 'created_at';
  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-1 text-xs">
      <span className="text-fg-subtle">date</span>
      <Select aria-label="Date field" value={field} onChange={(e) => onChange({ ...filters, date: { ...(filters.date ?? {}), field: e.target.value } })} className="h-6 py-0 text-xs">
        <option value="created_at">created</option>
        <option value="uploaded_at">uploaded</option>
        {fields.filter((f) => f.type === 'date').map((f) => <option key={f.id} value={`custom:${f.id}`}>{f.name}</option>)}
      </Select>
      <span className="text-fg-subtle">from</span>
      <Input type="date" aria-label="From date" value={filters.date?.from ?? ''} onChange={(e) => onChange({ ...filters, date: { ...(filters.date ?? {}), field, from: e.target.value || null } })} className="h-6 py-0 text-xs" />
      <span className="text-fg-subtle">to</span>
      <Input type="date" aria-label="To date" value={filters.date?.to ?? ''} onChange={(e) => onChange({ ...filters, date: { ...(filters.date ?? {}), field, to: e.target.value || null } })} className="h-6 py-0 text-xs" />
      {children}
    </div>
  );
}

/** Custom-field predicate rows (`custom_fields[]`): field, operator, value. */
export function CustomFieldRows<F extends AnyFilters>({ fields, filters, onChange, facets }: { fields: readonly CustomField[]; filters: F; onChange: (f: F) => void; facets: Facets['custom_fields'] | undefined }) {
  const list = filters.custom_fields ?? [];
  const set = (i: number, patch: Partial<(typeof list)[number]>) => onChange({ ...filters, custom_fields: list.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  if (fields.length === 0) return <span className="text-xs text-fg-subtle">No custom fields</span>;
  return (
    <div className="space-y-1">
      {list.map((c, i) => {
        const f = fields.find((x) => x.id === c.field_id);
        return (
          <div key={i} className="flex items-center gap-1">
            <Select aria-label="Field" value={c.field_id} onChange={(e) => set(i, { field_id: e.target.value, value: '' })} className="h-6 min-w-0 flex-1 py-0 text-xs">{fields.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select>
            <Select aria-label="Operator" value={c.op} onChange={(e) => set(i, { op: e.target.value as typeof c.op })} className="h-6 w-auto py-0 text-xs">
              {(f?.type === 'number' || f?.type === 'date' ? ['eq', 'gte', 'lte'] : f?.type === 'select' || f?.type === 'multi_select' ? ['eq', 'in'] : ['contains', 'eq']).map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
            {f?.type === 'select' || f?.type === 'multi_select' ? (
              <Select aria-label="Value" value={String(Array.isArray(c.value) ? c.value[0] ?? '' : c.value)} onChange={(e) => set(i, { value: c.op === 'in' ? [e.target.value] : e.target.value })} className="h-6 min-w-0 flex-1 py-0 text-xs">
                <option value="">—</option>
                {f.options?.map((o) => <option key={o.id} value={o.id}>{o.label}{facets?.[f.id]?.find((b) => b.value === o.id) ? ` (${facets[f.id]!.find((b) => b.value === o.id)!.count})` : ''}</option>)}
              </Select>
            ) : (
              <Input aria-label="Value" type={f?.type === 'number' ? 'number' : f?.type === 'date' ? 'date' : 'text'} value={String(c.value ?? '')} onChange={(e) => set(i, { value: f?.type === 'number' ? Number(e.target.value) : e.target.value })} className="h-6 min-w-0 flex-1 py-0 text-xs" />
            )}
            <button type="button" aria-label="Remove field filter" onClick={() => onChange({ ...filters, custom_fields: list.filter((_, j) => j !== i) })} className="text-fg-subtle hover:text-danger"><X size={12} /></button>
          </div>
        );
      })}
      <button type="button" className="flex items-center gap-1 text-xs text-accent hover:underline" onClick={() => onChange({ ...filters, custom_fields: [...list, { field_id: fields[0]!.id, op: fields[0]!.type === 'text' ? 'contains' : 'eq', value: '' }] })}><Plus size={12} /> field filter</button>
    </div>
  );
}
